import { useEffect, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext';

export default function Hero() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const nodes: { x: number; y: number; vx: number; vy: number }[] = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });
      nodes.forEach((a, i) => {
        nodes.slice(i + 1).forEach((b) => {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 160) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = isDark
              ? `rgba(20,184,200,${0.14 * (1 - dist / 160)})`
              : `rgba(13,122,138,${0.12 * (1 - dist / 160)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
        ctx.beginPath();
        ctx.arc(a.x, a.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? 'rgba(20,184,200,0.4)' : 'rgba(13,122,138,0.35)';
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [isDark]);

  return (
    <section
      id="home"
      className={`relative min-h-screen flex items-center overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0b1f3a]' : 'bg-[#e8f4f6]'
      }`}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2102fa6ceb300a8e64348ec321ee8cde.jpg"
          alt=""
          className={`w-full h-full object-cover object-top ${isDark ? 'opacity-20' : 'opacity-10'}`}
        />
        <div className={`absolute inset-0 ${
          isDark
            ? 'bg-gradient-to-r from-[#0b1f3a] via-[#0b1f3a]/85 to-[#0b1f3a]/50'
            : 'bg-gradient-to-r from-[#e8f4f6] via-[#e8f4f6]/90 to-[#e8f4f6]/60'
        }`}></div>
        <div className={`absolute inset-0 ${
          isDark
            ? 'bg-gradient-to-t from-[#0b1f3a] via-transparent to-[#0b1f3a]/50'
            : 'bg-gradient-to-t from-[#e8f4f6] via-transparent to-[#e8f4f6]/50'
        }`}></div>
      </div>

      {/* Animated canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-50" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8 ${
            isDark
              ? 'border-[#14b8c8]/30 bg-[#14b8c8]/10'
              : 'border-[#0d7a8a]/30 bg-[#0d7a8a]/10'
          }`}>
            <span className={`w-2 h-2 rounded-full animate-pulse ${isDark ? 'bg-[#14b8c8]' : 'bg-[#0d7a8a]'}`}></span>
            <span className={`text-xs font-medium tracking-widest uppercase ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}>Operational Intelligence Layer</span>
          </div>

          {/* Headline */}
          <h1 className={`text-5xl md:text-7xl font-thin leading-[1.05] mb-4 transition-colors duration-500 ${
            isDark ? 'text-white' : 'text-[#0b1f3a]'
          }`}>
            The Foundation
          </h1>
          <h2 className={`text-5xl md:text-7xl font-bold leading-[1.05] mb-6 transition-colors duration-500 ${
            isDark ? 'text-white' : 'text-[#0b1f3a]'
          }`}>
            Your Business<br />
            <span className={isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}>Cannot Afford</span><br />
            To Lose
          </h2>

          <p className={`text-lg md:text-xl leading-relaxed max-w-xl mb-10 transition-colors duration-500 ${
            isDark ? 'text-white/60' : 'text-[#0b1f3a]/60'
          }`}>
            Structured AI systems that capture demand, convert opportunities, and maintain workflow continuity — without disruption.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleScroll('#contact')}
              className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isDark
                  ? 'bg-[#14b8c8] text-[#0b1f3a] hover:bg-[#12a8b8]'
                  : 'bg-[#0d7a8a] text-white hover:bg-[#0a6878]'
              }`}
            >
              Build Your Foundation
              <i className="ri-arrow-right-line"></i>
            </button>
            <button
              onClick={() => handleScroll('#solutions')}
              className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium text-base transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isDark
                  ? 'border border-white/25 text-white hover:border-[#14b8c8]/50 hover:bg-[#14b8c8]/10'
                  : 'border border-[#0b1f3a]/25 text-[#0b1f3a] hover:border-[#0d7a8a]/50 hover:bg-[#0d7a8a]/10'
              }`}
            >
              Explore Systems
              <i className="ri-arrow-down-line"></i>
            </button>
          </div>

          {/* Stats */}
          <div className={`flex flex-wrap gap-10 mt-16 pt-10 border-t transition-colors duration-500 ${
            isDark ? 'border-white/10' : 'border-[#0b1f3a]/10'
          }`}>
            {[
              { value: '100%', label: 'Inquiry Capture Rate' },
              { value: '5+', label: 'Industries Served' },
              { value: '24/7', label: 'System Uptime' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className={`text-3xl font-bold ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}>{stat.value}</div>
                <div className={`text-sm mt-1 transition-colors duration-500 ${isDark ? 'text-white/50' : 'text-[#0b1f3a]/50'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className={`text-xs tracking-widest uppercase transition-colors duration-500 ${isDark ? 'text-white/30' : 'text-[#0b1f3a]/30'}`}>Scroll</span>
        <i className={`${isDark ? 'text-white/30' : 'text-[#0b1f3a]/30'}`}><i className="ri-arrow-down-line"></i></i>
      </div>
    </section>
  );
}